/**
 * The stock by depot of one article, opened from the list's quantity.
 *
 * It shows the depots the list counted: the quantity the user clicked is the
 * total of the chosen scope, so the dialog is narrowed to the same scope and
 * adds up to the same figure.
 *
 * The list row only knows the total and the number of depots, so the depot
 * lines are read when the dialog opens — the same fiche read, and the same
 * table, as the end of the article page.
 */

import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useResource } from "@hardauto/core-data";
import { Dialog, ErrorState, LoadingState } from "@hardauto/core-ui";

import type { ConsultationArticle, ConsultationRow } from "../../api";
import { NAMESPACE } from "../../i18n";
import { inSelection } from "../../consultation";
import { EMPTY_SCOPE, type ScopeSelection } from "../../scope";
import { StockDetail } from "../ConsultationArticleScreen";

export function StockDialog({
  load,
  row,
  scope,
  onClose,
}: {
  /** Reads the article — from what was prefetched on the way to the click, when it was. */
  load: (id: string) => Promise<ConsultationArticle>;
  row: ConsultationRow;
  scope: ScopeSelection;
  onClose: () => void;
}) {
  const { t } = useTranslation(NAMESPACE);
  const fetcher = useCallback(() => load(row.id), [load, row.id]);
  const article = useResource(fetcher);

  return (
    <Dialog
      open
      onClose={onClose}
      title={`${row.reference} · ${t("consultation.stockDialog.title")}`}
      description={row.designation}
      closeLabel={t("consultation.stockDialog.close")}
    >
      <div className="hardauto-stock-dialog">
        {article.failed ? (
          <ErrorState
            title={t("error.title")}
            onRetry={article.reload}
            retryLabel={t("error.retry")}
          />
        ) : article.data ? (
          <StockDetail
            article={{
              ...article.data,
              depots: article.data.depots.filter((line) => inSelection(line, scope)),
            }}
            // Every depot shown is inside the selection already, so none needs
            // the "in your context" marker.
            scope={EMPTY_SCOPE}
          />
        ) : (
          <LoadingState label={t("consultation.article.loading")} />
        )}
      </div>
    </Dialog>
  );
}
